import { CategorieIllustration } from "@/assets/images/svgIcons/illustrations/categorieIllustration"
import { Text } from "@/components/ui/baseUi/text"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import type { TemplateEntity } from "@/modules/template/entities/templateEntity"
import { useTemplatesTable } from "@/modules/template/hooks/useTemplate"
import { useTemplateFormSidebar } from "@/modules/template/hooks/useTemplateSidebar"
import {
  useTemplatePageSearchTerm,
  useTemplatePageTemplateTypeFilter,
} from "@/modules/template/stores/useTemplatePageStore"
import { TemplateTypeFilter } from "@/modules/template/types/templateType"
import { HUDatatable } from "@/modules/ui/components/dataTables/huDatatable"
import { createColumnHelper } from "@tanstack/react-table"
import dayjs from "dayjs"
import { useState } from "react"

const columnHelper = createColumnHelper<TemplateEntity>()

type TemplateTableProps = {
  className?: string
} & React.ComponentProps<"div">

export function TemplateTable({ className, ...props }: TemplateTableProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateEntity>()
  const templateTypeFilter = useTemplatePageTemplateTypeFilter()
  const searchTerm = useTemplatePageSearchTerm()
  const { navigateToTemplateForm } = useTemplateFormSidebar()
  const {
    data: templates,
    isLoading,
    pageSize,
    setPageSize,
    handlePreviousPage,
    handleNextPage,
    hasNextPage,
    hasPreviousPage,
    totalElements,
  } = useTemplatesTable({
    templateTypeFilter: templateTypeFilter,
    searchTerm: searchTerm,
  })

  const onSelectedTemplate = (e: TemplateEntity) => {
    navigateToTemplateForm(
      {
        templateId: e.id,
        onHide: () => {
          setSelectedTemplate(undefined)
        },
      },
      {
        replaceAllComponents: true,
      },
    )
    setSelectedTemplate(e)
  }

  const columns = [
    columnHelper.accessor("name", {
      header: () => <Text fontStyle="LL">{word("template.form.name.placeholder")}</Text>,
      cell: ({ row: { original: template } }) => <Text fontStyle="BM">{template.name}</Text>,
    }),
    columnHelper.accessor("description", {
      header: () => <Text fontStyle="LL">{word("template.form.description.placeholder")}</Text>,
      cell: ({ row: { original: template } }) => <Text fontStyle="BM">{template.description}</Text>,
    }),
    columnHelper.accessor("templateCategory", {
      header: () => <Text fontStyle="LL">{word("global.form.label.category")}</Text>,
      cell: ({ row: { original: template } }) => {
        if (!template.templateCategory) {
          return null
        }
        return <Text fontStyle="BM">{template.templateCategory.name}</Text>
      },
    }),
    columnHelper.accessor("creationDate", {
      header: () => <Text fontStyle="LL">{word("global.created")}</Text>,
      cell: ({ row: { original: template } }) => <Text fontStyle="BM">{dayjs(template.creationDate).format("L")}</Text>,
    }),
    columnHelper.accessor("lastUpdateDate", {
      header: () => <Text fontStyle="LL">{word("global.lastModified")}</Text>,
      cell: ({ row: { original: template } }) => (
        <Text fontStyle="BM">{dayjs(template.lastUpdateDate).format("L")}</Text>
      ),
    }),
  ]

  if (templates.length === 0) {
    return <TemplateTableEmptyView templateTypeFilter={templateTypeFilter} />
  }

  return (
    <div className={cn("flex flex-col gap-4", className)} {...props}>
      <HUDatatable
        data={templates}
        columns={columns}
        isLoading={isLoading}
        pageSize={pageSize}
        setPageSize={setPageSize}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        handleRowSelectionChange={(row: TemplateEntity) => {
          onSelectedTemplate(row)
        }}
        selectedRowIndex={templates.findIndex((template) => template.id === selectedTemplate?.id)}
        totalElements={totalElements}
      />
    </div>
  )
}

type TemplateTableEmptyViewProps = {
  templateTypeFilter: TemplateTypeFilter
  className?: string
} & React.ComponentProps<"div">

function TemplateTableEmptyView({ templateTypeFilter, className, ...props }: TemplateTableEmptyViewProps) {
  let subtitle: string

  switch (templateTypeFilter) {
    case TemplateTypeFilter.ALL:
      subtitle = word("templateType.empty.all")
      break
    case TemplateTypeFilter.SESSION:
      subtitle = word("templateType.empty.session")
      break
    case TemplateTypeFilter.EXERCISE:
      subtitle = word("templateType.empty.exercise")
      break
    case TemplateTypeFilter.WEEK:
      subtitle = word("templateType.empty.week")
      break
    default: {
      const exhaustiveCheck: never = templateTypeFilter
      throw new Error(`Invalid template type ${String(exhaustiveCheck)}`)
    }
  }

  return (
    <div
      className={cn(
        "flex flex-1 flex-col items-center justify-center rounded-3xl bg-surface-background-secondary",
        className,
      )}
      {...props}
    >
      <CategorieIllustration color="currentColor" />
      <Text fontStyle="LL">{subtitle}</Text>
    </div>
  )
}
